<template>
	<div v-if="isAdmin">
		<v-snackbar
			v-model="snackbar"
			:timeout="10000"
			:top="true"
			color="accent"
			class="black--text"
		>
			{{ snackbarText }}
			<v-btn class="blexon-button" small icon @click="snackbar = false">
				<v-icon>close</v-icon>
			</v-btn>
		</v-snackbar>
		<v-progress-linear v-show="dataLoading" color="accent" indeterminate></v-progress-linear>
		<v-container
			id="contentContainer"
			ref="contentContainer"
			class="content"
			grid-list-md
			fluid
		>
			<v-layout row wrap align-center>
				<v-flex shrink>
					<h1>Auslastung</h1>
				</v-flex>
				<v-flex shrink>
					<v-btn @click="goToAllFertiger()">Fertigerübersicht</v-btn>
				</v-flex>
				<v-spacer></v-spacer>
				<v-flex shrink>
					<v-layout row align-center>
						<v-flex>
							<date-picker
								:selectedDate="startDateString"
								label="Startdatum"
								v-on:updateSelectedDate="updateStartDate($event)"
							>
							</date-picker>
						</v-flex>
						<v-flex>
							<v-btn small class="blexon-button" @click="setDateToToday()">
								Heute
							</v-btn>
						</v-flex>
					</v-layout>
				</v-flex>
			</v-layout>
			<div v-show="!dataLoading">
				<v-tabs v-model="countryTab">
					<v-tab v-for="country in countryList" :key="country.id">
						{{ country.name }}
					</v-tab>
					<v-tab-item v-for="country in countryList" :key="country.id">
						<div class="capacity-scheduler">
							<div v-if="scrollLoading" class="loading-overlay">
								<v-progress-circular
									indeterminate
									color="accent"
									:size="50"
								></v-progress-circular>
								<span class="ml-3">wird geladen...</span>
							</div>

							<div
								class="scroll-viewport"
								ref="scrollViewport"
								:class="{ 'is-loading': scrollLoading }"
								@click.capture="preventClickIfDragging"
								@mousedown="!scrollLoading && startDragging($event)"
								@mousemove="onDragging"
								@mouseup="stopDragging"
								@mouseleave="stopDragging"
								@scroll="handleScroll"
							>
								<div class="scheduler-content">
									<div
										v-for="fertiger in getCountryFertigers(country.id)"
										:key="fertiger.id"
										class="fertiger-row"
									>
										<div class="fertiger-label">
											<h4 @click="goToFertiger(fertiger)">
												{{ fertiger.name }}
											</h4>
										</div>

										<div class="fertiger-days">
											<fertiger-capacity
												:fertiger="fertiger"
												:capacityData="getFertigerCapacityData(fertiger.id)"
												@updateCapacity="loadDataAndRenderCharts()"
												@showSnackbar="setSnackbarText($event)"
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</v-tab-item>
				</v-tabs>
			</div>
			<div v-if="dataLoading">
				<v-layout>
					<v-flex xs12>
						<v-card>
							<v-card-text> Daten werden geladen... </v-card-text>
						</v-card>
					</v-flex>
				</v-layout>
			</div>
		</v-container>
	</div>
</template>

<script>
import httpClient from "../store/httpClient";
import Chart from "chart.js";
import moment from "moment";
import DatePicker from "./datePicker.vue";
import FertigerCapacity from "./fertigerCapacityComponent.vue";

export default {
	components: {
		"date-picker": DatePicker,
		"fertiger-capacity": FertigerCapacity,
	},
	data() {
		return {
			snackbar: false,
			snackbarText: "",
			dataLoading: false,
			scrollLoading: false,
			startDateString: moment().format("YYYY-MM-DD"),
			startDate: new Date(),
			endDate: new Date(),
			dateMenu: false,
			capacityData: [],
			charts: [],
			isAdmin: false,
			countryTab: 0,
			dragStarted: false, // clicked. let's see if it becomes a drag
			isDragging: false, // actually dragging
			startX: 0,
			startY: 0,
			initialScrollLeft: 0,
			threshold: 5, // how many pixels of movement until it's a drag
			scrollLeft: 0,
			scrollThreshold: 200, // Pixels from edge to trigger load
			countryList: [
				{
					id: 1,
					name: "Schweiz",
				},
				{
					id: 2,
					name: "Deutschland",
				},
				{
					id: 3,
					name: "Österreich",
				},
			],
		};
	},
	mounted: function () {
		this.init();
		window.addEventListener("wheel", this.saveScrollPosition);
	},
	beforeDestroy() {
		localStorage.setItem("scroll-position", 0);
		window.removeEventListener("wheel", this.saveScrollPosition);
	},
	watch: {
		async countryTab(newTab, oldTab) {
			setTimeout(() => {
				this.$refs.scrollViewport[this.countryTab].scrollLeft = this.scrollLeft;
			}, 250);
		},
	},
	computed: {
		fertigers() {
			let fertiger = this.$store.state.fertiger;
			fertiger = fertiger.sort(function (a, b) {
				if (a.regionId < b.regionId) {
					return -1;
				} else if (a.regionId > b.regionId) {
					return 1;
				}
				return a.friendlyName - b.friendlyName;
			});
			return fertiger;
		},
	},
	methods: {
		async init() {
			await this.setAdmin();
			if (this.isAdmin) {
				try {
					this.initDates();
				} catch (err) {
					console.log(`Failed at loading dates: ${err}`);
					return;
				}
			}
		},

		async setAdmin() {
			const result = await httpClient().get("api/Capacity/IsUserAdmin");
			this.isAdmin = result.data;
		},

		goToAllFertiger(fertiger) {
			this.$router.push({ name: "fertiger" });
		},

		getCountryFertigers(countryId) {
			return this.fertigers.filter((f) => f.regionId === countryId);
		},

		initDates() {
			const currentDate = moment();
			this.updateStartDate(currentDate);
		},
		updateStartDate(date) {
			this.startDateString = moment(date).format("YYYY-MM-DD");
			this.startDate = new Date(date);
			// load from 7 days in the past
			this.startDate.setDate(this.startDate.getDate() - 7);
			this.endDate = new Date(date);
			// load the next 3 weeks
			this.endDate.setDate(this.endDate.getDate() + 28);
			this.loadDataAndRenderCharts();
		},
		formatShortDate(date) {
			return moment(date).format("dd DD.MM");
		},
		setDateToToday() {
			this.updateStartDate(moment());
		},

		getFertigerCapacityData(fertigerId) {
			return this.capacityData.filter((c) => c.fertigerId === fertigerId);
		},

		setSnackbarText(text) {
			this.snackbarText = text;
			this.snackbar = true;
		},

		saveScrollPosition() {
			let scrollTop = this.$refs.contentContainer.scrollTop;
			if (scrollTop > 0) {
				localStorage.setItem("scroll-position", scrollTop);
			}
		},
		setScrollPosition() {
			let top = localStorage.getItem("scroll-position");
			if (top !== null && parseInt(top, 10) > 0) {
				this.$refs.contentContainer.scrollTop = parseInt(top, 10);
			}
		},

		preventClickIfDragging(e) {
			// Stop events from getting to the children while dragging
			if (this.isDragging) {
				// Stop the event from propagating down to the children
				e.stopPropagation();
				// Prevent the default action
				e.preventDefault();
			}
		},

		startDragging(e) {
			this.dragStarted = true;
			const viewport = this.$refs.scrollViewport[this.countryTab];
			this.startX = e.pageX;
			this.startY = e.pageY;
			this.initialScrollLeft = viewport.scrollLeft;
		},
		onDragging(e) {
			if (!this.dragStarted) return;

			const deltaX = e.pageX - this.startX;
			const deltaY = e.pageY - this.startY;
			const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

			// Check if threshold for dragging has been reached => set isDragging
			if (!this.isDragging && distance > this.threshold) {
				this.isDragging = true;
			}

			if (this.isDragging) {
				e.preventDefault();
				const viewport = this.$refs.scrollViewport[this.countryTab];
				requestAnimationFrame(() => {
					viewport.scrollLeft = this.initialScrollLeft - deltaX;
				});
			}
		},
		stopDragging() {
			this.dragStarted = false;
			if (this.isDragging) {
				// Short timeout to ensure "click" events don't fire immediately after drag
				setTimeout(() => {
					this.isDragging = false;
					this.scrollLeft = this.$refs.scrollViewport[this.countryTab].scrollLeft;
				}, 50);
			}
		},
		handleScroll(e) {
			const el = e.target;

			if (this.dataLoading || this.scrollLoading) return;

			// 1. Check Right Edge (Load Future)
			if (el.scrollWidth - el.scrollLeft - el.clientWidth < this.scrollThreshold) {
				this.loadMoreData("future");
			}

			// 2. Check Left Edge (Load Past)
			if (el.scrollLeft < this.scrollThreshold) {
				this.loadMoreData("past");
			}
		},

		async loadDataAndRenderCharts() {
			this.dataLoading = true;
			try {
				await this.loadCapacityData(this.startDate, this.endDate, "new");
				if (this.capacityData.length <= 0) {
					return;
				}
				await this.createCharts();
			} catch (e) {
				console.log(`Fehler beim Laden von Kapazitätsdaten. ${e}`);
			} finally {
				this.dataLoading = false;
				setTimeout(() => {
					this.scrollLeft = 750;
					this.$refs.scrollViewport[this.countryTab].scrollLeft = this.scrollLeft;
					this.setScrollPosition();
				}, 250);
			}
		},

		async loadCapacityData(startDate, endDate, direction) {
			const response = await httpClient().get(
				`api/Capacity/GetCapacityData?startDate=${startDate.toJSON()}&endDate=${endDate.toJSON()}`
			);
			const formattedData = response.data.map((c) => ({
				fertigerId: c.fertigerId,
				date: moment(c.date),
				isDeliveryDay: c.isDeliveryDay,
				isWorkDay: c.isWorkDay,
				hasCapacities: c.hasCapacities,
				laserCapacity: moment.duration(c.laserCapacity),
				laserUsedCapacity: moment.duration(c.laserUsedCapacity),
				bendingCapacity: moment.duration(c.bendingCapacity),
				bendingUsedCapacity: moment.duration(c.bendingUsedCapacity),
				weldingCapacity: moment.duration(c.weldingCapacity),
				weldingUsedCapacity: moment.duration(c.weldingUsedCapacity),
				isFertigerHoliday: c.isFertigerHoliday,
				isPublicHoliday: c.isPublicHoliday,
			}));
			if (direction === "new") {
				this.capacityData.length = 0;
				this.capacityData.push(...formattedData);
			} else if (direction === "future") {
				this.capacityData.push(...formattedData);
			} else if (direction === "past") {
				this.capacityData = [...formattedData, ...this.capacityData];
			}
		},

		async loadMoreData(direction) {
			if (this.dataLoading || this.scrollLoading) return;

			const container = this.$refs.scrollViewport[this.countryTab];
			const previousScrollWidth = container.scrollWidth;

			try {
				this.scrollLoading = true;

				let start = this.startDate;
				let end = this.endDate;
				if (direction === "past") {
					start = moment(this.startDate).add(-7, "days").toDate();
					end = moment(this.startDate).add(-1, "day").toDate();
					this.startDate = start;
				} else if (direction === "future") {
					start = moment(this.endDate).add(1, "days").toDate();
					end = moment(this.endDate).add(7, "day").toDate();
					this.endDate = end;
				}

				await this.loadCapacityData(start, end, direction);
				await this.createCharts();

				if (direction === "past") {
					await this.$nextTick();
					const newScrollWidth = container.scrollWidth;
					const widthDifference = newScrollWidth - previousScrollWidth;
					const previousScrollLeft = container.scrollLeft;
					container.scrollLeft = previousScrollLeft + widthDifference;
					this.scrollLeft = container.scrollLeft;
				} else if (direction === "future") {
					this.scrollLeft = container.scrollLeft;
				}
			} catch (err) {
				console.log("Error while loading more data:", err);
				this.setSnackbarText("Daten konnten nicht nachgeladen werden.");
			} finally {
				this.scrollLoading = false;
			}
		},

		async createCharts() {
			// Destroy all charts
			var i;
			for (i = 0; i < this.charts.length; i++) {
				this.charts[i].destroy();
			}
			this.charts.length = 0;
			let areaRed = "rgba(255, 99, 132, 0.2)";
			let borderRed = "rgba(255, 99, 132, 1)";
			let areaGreen = "rgba(75, 192, 192, 0.2)";
			let borderGreen = "rgba(75, 192, 192, 1)";
			// Get the overall maximum timespan
			// Set it as a reference, so that all charts have the same scale
			var maxLaserCapacity = this.capacityData.reduce(
				(max, c) =>
					c.laserCapacity.asMilliseconds() > max ? c.laserCapacity.asMilliseconds() : max,
				this.capacityData[0].laserCapacity.asMilliseconds()
			);
			var maxLaserUsedCapacity = this.capacityData.reduce(
				(max, c) =>
					c.laserUsedCapacity.asMilliseconds() > max
						? c.laserUsedCapacity.asMilliseconds()
						: max,
				this.capacityData[0].laserUsedCapacity.asMilliseconds()
			);
			var maxBendingCapacity = this.capacityData.reduce(
				(max, c) =>
					c.bendingCapacity.asMilliseconds() > max
						? c.bendingCapacity.asMilliseconds()
						: max,
				this.capacityData[0].bendingCapacity.asMilliseconds()
			);
			var maxBendingUsedCapacity = this.capacityData.reduce(
				(max, c) =>
					c.bendingUsedCapacity.asMilliseconds() > max
						? c.bendingUsedCapacity.asMilliseconds()
						: max,
				this.capacityData[0].bendingUsedCapacity.asMilliseconds()
			);
			var maxWeldingCapacity = this.capacityData.reduce(
				(max, c) =>
					c.weldingCapacity.asMilliseconds() > max
						? c.weldingCapacity.asMilliseconds()
						: max,
				this.capacityData[0].weldingCapacity.asMilliseconds()
			);
			var maxWeldingUsedCapacity = this.capacityData.reduce(
				(max, c) =>
					c.weldingUsedCapacity.asMilliseconds() > max
						? c.weldingUsedCapacity.asMilliseconds()
						: max,
				this.capacityData[0].weldingUsedCapacity.asMilliseconds()
			);
			var maxCapacity = Math.max(
				maxLaserCapacity,
				maxBendingCapacity,
				maxWeldingCapacity,
				maxLaserUsedCapacity,
				maxBendingUsedCapacity,
				maxWeldingUsedCapacity
			);
			this.capacityData.forEach((f) => {
				if (f.hasCapacities) {
					var thisDate = moment(f.date);
					// Laser variables
					var laserCapacity = moment.duration(f.laserCapacity).asMilliseconds();
					var laserUsed = moment.duration(f.laserUsedCapacity).asMilliseconds();
					var laserUsedColor = laserUsed > laserCapacity ? areaRed : areaGreen;
					var laserUsedBorderColor = laserUsed > laserCapacity ? borderRed : borderGreen;
					var laserId = `chart${f.fertigerId}${thisDate.unix()}Laser`;
					// Bending variables
					var bendingCapacity = moment.duration(f.bendingCapacity).asMilliseconds();
					var bendingUsed = moment.duration(f.bendingUsedCapacity).asMilliseconds();
					var bendingUsedColor = bendingUsed > bendingCapacity ? areaRed : areaGreen;
					var bendingUsedBorderColor =
						bendingUsed > bendingCapacity ? borderRed : borderGreen;
					var bendingId = `chart${f.fertigerId}${thisDate.unix()}Bending`;
					// Welding variables
					var weldingCapacity = moment.duration(f.weldingCapacity).asMilliseconds();
					var weldingUsed = moment.duration(f.weldingUsedCapacity).asMilliseconds();
					var weldingUsedColor = weldingUsed > weldingCapacity ? areaRed : areaGreen;
					var weldingUsedBorderColor =
						weldingUsed > weldingCapacity ? borderRed : borderGreen;
					var weldingId = `chart${f.fertigerId}${thisDate.unix()}Welding`;

					this.createChart(
						laserId,
						laserCapacity,
						laserUsed,
						laserUsedColor,
						laserUsedBorderColor,
						maxCapacity
					);
					this.createChart(
						bendingId,
						bendingCapacity,
						bendingUsed,
						bendingUsedColor,
						bendingUsedBorderColor,
						maxCapacity
					);
					this.createChart(
						weldingId,
						weldingCapacity,
						weldingUsed,
						weldingUsedColor,
						weldingUsedBorderColor,
						maxCapacity
					);
				}
			});
		},
		createChart(id, capacity, used, usedColor, usedBorderColor, maxCapacity) {
			let areaBlue = "rgba(54, 162, 235, 0.2)";
			let borderBlue = "rgba(54, 162, 235, 1)";

			// Register custome positioner for tooltip
			// This is necessary to avoid tooltips being cut off by small canvas size
			Chart.Tooltip.positioners.custom = function (elements, position) {
				if (!elements.length) {
					return false;
				}
				return {
					// in the middle of the x-Axis
					x: elements[0]._chart.width / 2,
					// factor 0.85 seems to center it pretty well in the middle of the chart
					y: elements[0]._chart.height * 0.85,
				};
			};
			var ctx = document.getElementById(id).getContext("2d");
			var barChart = new Chart(ctx, {
				type: "bar",
				data: {
					labels: ["Kapazität", "Auslastung"],
					datasets: [
						{
							data: [capacity, used],
							backgroundColor: [
								areaBlue,
								usedColor, // depending on value either green or red
							],
							borderColor: [
								borderBlue,
								usedBorderColor, // depending on value either green or red
							],
							borderWidth: 1,
						},
					],
				},
				options: {
					layout: {
						padding: {
							bottom: 2,
						},
					},
					legend: {
						display: false,
					},
					scales: {
						yAxes: [
							{
								gridLines: [
									{
										display: false,
									},
								],
								ticks: {
									display: false,
									beginAtZero: true,
									max: maxCapacity,
								},
							},
						],
						xAxes: [
							{
								gridLines: [
									{
										display: false,
									},
								],
								ticks: {
									display: false,
								},
							},
						],
					},
					tooltips: {
						// using custom tooltip positioner from above
						position: "custom",
						callbacks: {
							label: function (tooltipItem, data) {
								var milliseconds = tooltipItem.yLabel;
								var hours = moment.duration(milliseconds).get("hours");
								var minutes = moment.duration(milliseconds).get("minutes");
								var days = moment.duration(milliseconds).days();
								var displayString = `${hours}h ${minutes}min`;
								if (days > 0) {
									displayString = `${days}d ${displayString}`;
								}
								return displayString;
							},
						},
					},
				},
			});
			this.charts.push(barChart);
		},
	},
};
</script>

<style scoped>
.capacity-scheduler {
	position: relative;
	border: 1px solid #ddd;
	overflow: hidden;
	min-height: 200px;
}

.scroll-viewport {
	overflow-x: auto;
	overflow-y: hidden;
	cursor: grab;
	user-select: none;
	transition: opacity 0.3s ease;
}

.scroll-viewport:active {
	cursor: grabbing;
}

.scroll-viewport.is-loading {
	pointer-events: none;
	opacity: 0.6;
	cursor: default;
}

.scheduler-content {
	display: flex;
	flex-direction: column;
	min-width: max-content;
}

.loading-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(255, 255, 255, 0.7);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 100;
	backdrop-filter: blur(2px);
}

.fertiger-row {
	display: flex;
	border-bottom: 1px solid #eee;
}

.fertiger-label {
	position: sticky;
	left: 0;
	z-index: 2;
	background: white;
	width: 150px;
	min-width: 150px;
	padding: 10px;
	display: flex;
	align-items: center;
	border-right: 2px solid #ddd;
}

.fertiger-days {
	display: flex;
}
</style>
